/* eslint-disable react/jsx-no-constructed-context-values */

import * as Sentry from '@sentry/react';
import type { User } from '@supabase/supabase-js';
import colorJS from 'color';
import { createContext, useEffect, useMemo } from 'react';

import Loader from '@/components/Loader';
import MigrationModal from '@/components/MigrationModal/MigrationModal';
import { adjustColor } from '@/helpers/adjust-color';
import { useGetDisciplines } from '@/hooks/queries/useDisciplines';
import { useGetDivisions } from '@/hooks/queries/useDivisions';
import { useGetSiteSettings } from '@/hooks/queries/useSiteSettings';
import { useAuth, useCurrentUser } from '@/hooks/useAuth';
import { useMigrationCheck } from '@/hooks/useMigrationCheck';
import { useAlignmentStore } from '@/stores/alignmentStore';

interface SiteSettings {
	id: string;
	statement_title: string | null;
	statement: string;
	primary_color: string;
	banner: string | null;
	logo: string | null;
	label_config?: Record<string, string> | null;
}

interface IGlobalContext {
	user: User | null;
	siteSettings: SiteSettings | undefined;
	disciplines: Discipline[] | undefined;
	divisions: Division[] | undefined;
	currentUser: ReturnType<typeof useCurrentUser>['data'];
}

const GlobalContext = createContext({} as IGlobalContext);

const GlobalContextProvider = ({ ...props }) => {
	const { children } = props;
	const { user, initializing } = useAuth();
	const { data: currentUser } = useCurrentUser(!initializing);

	// TEMPORARY: Check if user needs migration from Firebase to Supabase
	// @see MIGRATION-REMOVAL.md for removal instructions
	const {
		needsMigration,
		firebaseUser,
		isChecking: isMigrationChecking,
		onMigrationComplete,
	} = useMigrationCheck();

	const { data: disciplinesData, isLoading: isDisciplinesLoading } = useGetDisciplines();
	const { data: divisionsData, isLoading: isDivisionsLoading } = useGetDivisions();
	const { data: siteSettingsData, isLoading: isSiteSettingsLoading } = useGetSiteSettings();

	const isSettingsLoading = isDisciplinesLoading || isDivisionsLoading || isSiteSettingsLoading;

	// Transform disciplines and divisions with isSelected and update alignment store
	const settingsDivisionDisciplineData = useMemo(() => {
		if (!disciplinesData || !divisionsData) return null;

		// Add isSelected to disciplines
		const disciplines: DisciplineWithSelection[] = disciplinesData.map((discipline) => ({
			...discipline,
			isSelected: true,
		}));

		// Add isSelected to divisions
		const divisions: DivisionWithSelection[] = divisionsData.map((division) => ({
			...division,
			isSelected: true,
		}));

		// Update alignment store
		useAlignmentStore.setState({ disciplines });
		useAlignmentStore.setState({ divisions });

		return { disciplines, divisions, siteSettings: siteSettingsData ?? undefined };
	}, [disciplinesData, divisionsData, siteSettingsData]);

	useEffect(() => {
		if (settingsDivisionDisciplineData) {
			const root = document.documentElement;
			const themeColor = settingsDivisionDisciplineData.siteSettings?.primary_color;

			root.style.setProperty('--org-color', themeColor ?? 'var(--elevate-primary)');
			if (themeColor) {
				root.style.setProperty(
					'--org-color-alt',
					adjustColor(themeColor, -20) ?? 'var(--elevate-primary)'
				);
			}
			root.style.setProperty(
				'--org-color-opacity',
				colorJS(themeColor).fade(0.5).toString() ?? 'var(--elevate-primary)'
			);
		}
	}, [settingsDivisionDisciplineData]);

	useEffect(() => {
		if (
			import.meta.env.MODE !== 'development' &&
			window.location.hostname !== 'elevate-development.vercel.app'
		) {
			Sentry.setUser({ email: user?.email ?? 'anonymous' });
		}
	}, [user]);

	// Show loader while checking auth state and migration status
	// Don't wait for migration check to complete before showing content
	if (isSettingsLoading || initializing) {
		return <Loader />;
	}

	return (
		<GlobalContext.Provider
			value={{
				siteSettings: settingsDivisionDisciplineData?.siteSettings,
				disciplines: settingsDivisionDisciplineData?.disciplines,
				divisions: settingsDivisionDisciplineData?.divisions,
				user,
				currentUser,
			}}
		>
			{children}
			{/* TEMPORARY: Migration modal for Firebase users
			    @see MIGRATION-REMOVAL.md for removal instructions */}
			{!isMigrationChecking && needsMigration && (
				<MigrationModal
					isOpen={needsMigration}
					firebaseUser={firebaseUser}
					onMigrationComplete={onMigrationComplete}
				/>
			)}
		</GlobalContext.Provider>
	);
};

export default GlobalContext;

export { GlobalContextProvider };
